/**
 * The app-wide loading spinner.
 *
 * Renders the Ano mark (two overlapping lens outlines) as an inking
 * animation: each lens first strokes its outline (trace), then fills in
 * with currentColor, then fades. Combined effect reads as "ink being
 * applied" — legible at the small icon sizes most consumers use (10–24px).
 * Cycle is 1.6s, staggered 180ms left → right.
 *
 * Color is inherited from the parent's `color` (currentColor). Set it via
 * a Tailwind `text-*` class on a parent or directly on the icon. Default
 * size 16, default speed 1.6s. Honors `prefers-reduced-motion` (falls
 * back to a static dim mark).
 *
 * Export name `LoadingIcon` is preserved so the ~30 call sites across
 * the app don't need to change their imports.
 */
import { useEffect } from "react";
import { cn } from "@/lib/utils";

const VIEWBOX = "0 0 426 319";

// Left lens (outer outline + inner cutout via evenodd fill rule).
const LEFT_PATH =
  "M212.362 207.064C283.204 145.993 318.39 70.6834 299.325 48.5674C274.54 " +
  "19.817 200.587 50.1576 129.745 111.229C58.9032 172.3 15.8883 241.509 " +
  "41.3088 270.997C59.1037 291.638 141.52 268.135 212.362 207.064ZM202.543 " +
  "196.256C159.228 233.596 105.125 243.663 91.4372 227.785C71.8831 205.102 " +
  "95.6772 159.878 138.992 122.537C182.307 85.1964 230.131 69.6693 249.196 " +
  "91.7848C263.862 108.797 245.858 158.915 202.543 196.256Z";

// Right lens (mirror of left, shifted along the diagonal).
const RIGHT_PATH =
  "M297.728 206.415C368.571 145.344 403.757 70.0346 384.691 47.9189C359.906 " +
  "19.1687 285.954 49.51 215.112 110.581C144.27 171.652 101.255 240.861 " +
  "126.675 270.349C144.469 290.99 226.886 267.486 297.728 206.415ZM287.91 " +
  "195.608C244.595 232.948 190.493 243.015 176.804 227.137C157.25 204.455 " +
  "181.043 159.229 224.358 121.889C267.673 84.5479 315.498 69.0214 334.563 " +
  "91.1371C349.228 108.149 331.225 158.267 287.91 195.608Z";

const STYLE_ID = "ano-spinner-style";

// Trace + ink animation: each lens draws its outline (stroke-dasharray),
// fills in with currentColor, holds, then fades. Staggered L → R so the
// two lenses ink in sequence, then the cycle restarts.
const css = `
[data-ano-spinner] [data-p] {
  fill: currentColor;
  stroke: currentColor;
  fill-rule: evenodd;
  /* Render stroke at a constant pixel size regardless of SVG scale —
     stroke-width in a 426-unit viewBox renders sub-pixel at icon size
     12 otherwise. */
  vector-effect: non-scaling-stroke;
  stroke-width: 1;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 2000;
  stroke-dashoffset: 2000;
  fill-opacity: 0;
  opacity: 0;
  animation-name: ano-spinner-trace;
  animation-duration: var(--ano-spin-dur, 1.6s);
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}
[data-ano-spinner] [data-p="L"] { animation-delay: 0s; }
[data-ano-spinner] [data-p="R"] { animation-delay: 220ms; }
@keyframes ano-spinner-trace {
  0%   { opacity: 0; fill-opacity: 0; stroke-dashoffset: 2000; }
  15%  { opacity: 1; }
  40%  { opacity: 1; fill-opacity: 0; stroke-dashoffset: 0; }
  60%  { opacity: 1; fill-opacity: 1; stroke-dashoffset: 0; }
  80%  { opacity: 1; fill-opacity: 1; stroke-dashoffset: 0; }
  100% { opacity: 0; fill-opacity: 0; stroke-dashoffset: 0; }
}
@media (prefers-reduced-motion: reduce) {
  [data-ano-spinner] [data-p] {
    animation: none;
    stroke-dashoffset: 0;
    fill-opacity: 0.5;
    opacity: 0.5;
  }
}
`;

function ensureStyle(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = css;
  document.head.appendChild(el);
}

interface LoadingIconProps {
  size?: number | string;
  /** Cycle duration in seconds. Default 1.6. */
  speed?: number;
  className?: string;
  style?: React.CSSProperties;
  /** Accessible label. Default "Loading". */
  ariaLabel?: string;
}

function LoadingIcon({
  size = 16,
  speed = 1.6,
  className,
  style,
  ariaLabel = "Loading",
}: LoadingIconProps) {
  // Inject the keyframes/style tag once per page. ensureStyle is a
  // no-op if the tag is already in <head>, so this is safe to fire
  // on every mount; React strict mode double-mounts run it twice
  // and the second call is a no-op.
  useEffect(() => {
    ensureStyle();
  }, []);

  return (
    <svg
      data-ano-spinner=""
      role="img"
      aria-label={ariaLabel}
      width={size}
      height={size}
      viewBox={VIEWBOX}
      fill="currentColor"
      // `loading-icon` is a no-op selector hook kept for back-compat
      // with tests + external `.loading-icon` queries; the actual
      // animation lives on `[data-ano-spinner]` paths above.
      className={cn("loading-icon text-primary", className)}
      style={{
        ["--ano-spin-dur" as string]: `${speed}s`,
        display: "inline-block",
        verticalAlign: "middle",
        ...style,
      }}
    >
      <path d={LEFT_PATH} data-p="L" />
      <path d={RIGHT_PATH} data-p="R" />
    </svg>
  );
}

export { LoadingIcon };
